import * as React from "react";

import { invariant } from "../lib/misc";

// A context type that React 18 and React 19 typings both accept. This package
// compiles against one `@types/react` while its consumers may use the other,
// and each major's `ReactNode`, `ProviderProps` and `Context` are mutually
// incompatible. So the types below avoid `ReactNode` entirely: providers and
// consumers are typed as components that render `null`, and the context is
// callable with a `$$typeof` tag, which React 19 types require (`<Ctx value>`).
interface NeutralProvider<ValueType> {
  (props: { value: ValueType; children?: unknown }): null;
  readonly $$typeof: symbol;
}

interface NeutralConsumer<ValueType> {
  (props: { children: (value: ValueType) => unknown }): null;
  readonly $$typeof: symbol;
}

export interface Context<ValueType> extends NeutralProvider<ValueType> {
  Provider: NeutralProvider<ValueType>;
  Consumer: NeutralConsumer<ValueType>;
  displayName?: string | undefined;
}

/**
 * What `useRequiredContext` accepts: any React context, from either major.
 */
interface ContextLike<ValueType> {
  Provider: (props: { value: ValueType }) => unknown;
  displayName?: string | undefined;
}

function isReactContext<ValueType>(
  context: ContextLike<ValueType>,
): context is React.Context<ValueType> {
  return "Consumer" in context;
}

/**
 * Lightweight wrapper around `React.createContext` that requires a
 * `displayName`, which is useful for debugging in React DevTools.
 */
export function createContext<ValueType>(
  displayName: string,
  initialValue: ValueType,
): Context<ValueType>;
export function createContext<ValueType>(
  displayName: string,
  initialValue: ValueType | null,
): Context<ValueType | null>;
export function createContext<ValueType>(
  displayName: string,
): Context<ValueType | undefined>;

// NOTE (Chance 2024-06-20): `@__NO_SIDE_EFFECTS__` is an esbuild and rollup
// convention to indicate that this function has no side effects. By default
// esbuild might not tree-shake code that relies on this function because it
// sets `displayName`, and the bundler doesn't know whether that has side
// effects (ex, if there's a setter for the property). This annotation tells the
// bundler it's tree-shakable. Note too that the position in which the comment
// appears is important!
// https://github.com/evanw/esbuild/issues/3149#issuecomment-1585820844

/* @__NO_SIDE_EFFECTS__ */ export function createContext<ValueType>(
  displayName: string,
  initialValue?: ValueType,
) {
  const ctx = React.createContext(initialValue);
  ctx.displayName = displayName;
  return ctx;
}

export function useRequiredContext<ValueType>(
  context: ContextLike<ValueType>,
): NonNullable<ValueType> {
  invariant(
    isReactContext(context),
    `useRequiredContext: ${context.displayName ?? "Unknown context"} is not a React context.`,
  );
  const ctx = React.useContext(context);
  // Inline nullish check (not isNotNullish): invariant(asserts condition) only
  // narrows from the condition expression itself, not from a boolean helper.
  invariant(
    ctx !== null && ctx !== undefined,
    `useRequiredContext: ${context.displayName ?? "Unknown context"} is not defined. Caller component must be rendered in a provider.`,
  );
  return ctx;
}
